/* eslint-disable import/prefer-default-export */
import type { SelectOption } from '@src/components/common/select'
import {
  AWSInfo,
  AzureInfo,
  CloudTypeEnum,
  GCPAccountInfo,
  OktaInfo
} from '@src/endpoints/services/base.service'
import type { Model } from '@src/redux/reducers/organization/models'
import { SnapshotEnum } from '../models'
export * from './snapshot.errors.columns'
export const intervalRefresh = (longInterval: boolean) =>
  longInterval ? 60000 : 5000

type OrganizationLabels = {
  [key in CloudTypeEnum]: OrganizationLabel
}

export const OrganizationLabels: OrganizationLabels = {
  [CloudTypeEnum.AWS]: {
    name: 'AWS',
    orgRootAccount: 'Organization Name',
    subOrganization: 'Organization Unit',
    orgId: 'Organization ID',
    orgUnitId: 'Organization Unit ID',
    acctId: 'Account ID',
    orgAccount: 'Account',
    fields: {
      name: {
        label: 'Unique Name',
        placeholder: 'Enter Unique name...'
      },
      role_arn: {
        label: 'Role ARN',
        placeholder: 'Enter Role ARN...'
      },
      external_id: {
        label: 'External ID',
        placeholder: 'Enter External ID...'
      },
      cloud_trail_arn: {
        label: 'CloudTrail ARN',
        placeholder: 'Enter CloudTrail ARN...'
      },
      ct_log_archive_account_id: {
        label: 'CloudTrail Bucket Account ID',
        placeholder: 'CloudTrail Bucket Account ID...'
      },
      sns_topic_arn: {
        label: 'SNS Topic ARN',
        placeholder: 'Enter SNS Topic ARN...'
      }
    }
  },
  [CloudTypeEnum.AZURE]: {
    name: 'Microsoft Azure',
    orgRootAccount: 'Organization Name',
    subOrganization: 'Organization Name',
    orgAccount: 'Subscription',
    fields: {
      name: {
        label: 'Name',
        placeholder: 'Enter Unique name...'
      },
      app_client_id: {
        label: 'Application ID',
        placeholder: 'Enter Application ID...'
      },
      app_secret: {
        label: 'Application Secret',
        placeholder: 'Enter Application Secret...'
      },
      tenant_id: {
        label: 'Azure Tenant ID',
        placeholder: 'Enter Azure Tenant ID...'
      }
    }
  },
  [CloudTypeEnum.GCP]: {
    name: 'Google Cloud',
    orgRootAccount: 'Organization Name',
    subOrganization: 'Folder',
    orgAccount: 'Project',
    fields: {
      organization_id: {
        label: 'Organization ID',
        placeholder: 'Enter Organization ID...'
      },
      domain: { label: 'Domain name', placeholder: 'Enter Domain name...' },

      name: {
        label: 'Name',
        placeholder: 'Enter Unique name...'
      },
      admin_email: {
        label: 'Email with Admin Privileges',
        placeholder: 'Enter Email with Admin Privileges...'
      }
    }
  },
  [CloudTypeEnum.IDP_AZURE_AD]: {
    name: 'Azure Active Directory',
    orgRootAccount: 'Active Directory',
    subOrganization: '',
    orgAccount: '',
    fields: {
      name: {
        label: 'Active Directory Name',
        placeholder: 'Enter Active Directory Name...'
      },
      tenant_id: {
        label: 'Directory ID',
        placeholder: 'Enter Directory ID...'
      },
      app_client_id: {
        label: 'Application ID',
        placeholder: 'Enter Application ID...'
      },
      app_secret: {
        label: 'Application Secret',
        placeholder: 'Enter Application Secret...'
      },
      cloud_type: {
        label: 'SAAS Application Cloud Type',
        placeholder: 'Select SAAS Application Cloud Type...'
      },
      app_instance_id: {
        label: 'SAAS Application ID',
        placeholder: 'Enter SAAS Application ID...'
      }
    }
  },
  [CloudTypeEnum.IDP_OKTA]: {
    name: 'Okta',
    orgRootAccount: 'Source Name',
    subOrganization: '',
    orgAccount: '',
    fields: {
      name: {
        label: 'Account Name',
        placeholder: 'Enter account Name...'
      },
      org_url: {
        label: 'Organization Url',
        placeholder: 'Enter Organization Url...'
      },
      token: {
        label: 'Application Token',
        placeholder: 'Enter Application Token...'
      },
      application_id: {
        label: 'Application ID',
        placeholder: 'Enter Application ID...'
      },
      application_type: {
        label: 'SAAS Application Type',
        placeholder: 'Select SAAS Application Type...'
      }
    }
  },
  [CloudTypeEnum.CLOUD_TYPE_UNKNOWN]: {
    name: 'UNKNOWN',
    orgRootAccount: 'Name',
    subOrganization: 'UNKNOWN',
    orgAccount: 'UNKNOWN',
    fields: {}
  }
}

export const cloudTypesOptions: SelectOption<CloudTypeEnum>[] = [
  { label: 'Amazon Web Services', value: CloudTypeEnum.AWS }
]

export const cloudTabs = [
  { label: getLabel(CloudTypeEnum.AWS, 'name'), value: CloudTypeEnum.AWS },
  { label: getLabel(CloudTypeEnum.GCP, 'name'), value: CloudTypeEnum.GCP },
  { label: getLabel(CloudTypeEnum.AZURE, 'name'), value: CloudTypeEnum.AZURE }
]

export const idpTabs = [
  {
    label: getLabel(CloudTypeEnum.IDP_AZURE_AD, 'name'),
    value: CloudTypeEnum.IDP_AZURE_AD
  },
  {
    label: getLabel(CloudTypeEnum.IDP_OKTA, 'name'),
    value: CloudTypeEnum.IDP_OKTA
  }
]

export const snapshotTabs = [
  {
    label: 'Last Snapshot',
    value: SnapshotEnum.LAST_SNAPSHOT
  },
  {
    label: 'History',
    value: SnapshotEnum.HISTORY
  }
]

interface FormField {
  label: string
  placeholder?: string
  isOptional?: boolean
}

type KeyFields = Omit<
  Model,
  'original' | 'awsInfo' | 'azureInfo' | 'gcpInfo' | 'includeAccountIdList'
> &
  AWSInfo &
  AzureInfo &
  GCPAccountInfo &
  OktaInfo

type FormFields = { [key in keyof KeyFields]?: FormField }

export interface OrganizationLabel {
  name: string
  orgRootAccount: string
  subOrganization: string
  orgAccount: string
  fields: FormFields
  orgId?: string
  acctId?: string
  orgUnitId?: string
}

export function getLabel(
  cloudType: CloudTypeEnum,
  byLabel: keyof OrganizationLabel
): string {
  const lablesByType = OrganizationLabels[cloudType] || {}
  return lablesByType[byLabel] || ''
}

export function getFieldsByType(cloudType: CloudTypeEnum): FormFields {
  const labels = OrganizationLabels[cloudType] || ({} as OrganizationLabel)
  return labels?.fields || {}
}

export function getFieldProps(
  cloudType: CloudTypeEnum,
  label: keyof OrganizationLabels[typeof cloudType]['fields']
): FormField {
  const fields = getFieldsByType(cloudType) || {}
  return fields[label]
}
